import { FormItem } from '@/components/FormItem'
import { FormList } from '@/components/FormList'
import { Select } from '@/components/selects/Select'
import { useFormInstance } from '@/hooks/useFormInstance'
import { useWatch } from '@/hooks/useWatch'
import { type ReportDataType } from '@/services/types/notifications/mockNotifications'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EmailReportFilterEvaluation,
  EmailReportFilterSource,
  FilterComponent,
  type EmailReportFilterPropsUnion,
} from './FilterComponent'
import styles from './styles.module.scss'

export type FilterOptions = Array<{
  label: string
  value: EmailReportFilterSource
}>

export const I18N_PREFIX = 'pages.emailReport.form.sections.filters'

interface CustomFiltersProps {
  name: string
  selectedReports?: ReportDataType[]
  disabled?: boolean
}

export function CustomFilters({ name, selectedReports, disabled }: CustomFiltersProps) {
  const { t } = useTranslation()

  const FILTER_OPTIONS = [
    {
      label: t(`${I18N_PREFIX}.filterLabels.folders`),
      value: EmailReportFilterSource.folder,
    },
    {
      label: t(`${I18N_PREFIX}.filterLabels.locations`),
      value: EmailReportFilterSource.location,
    },
    {
      label: t(`${I18N_PREFIX}.filterLabels.publishers`),
      value: EmailReportFilterSource.publisher,
    },
  ]

  const [filterOptions, setFilterOptions] = useState(FILTER_OPTIONS)

  const form = useFormInstance()
  const filters = useWatch<EmailReportFilterPropsUnion[]>(name, form)

  useEffect(() => {
    if (!filters) return

    setFilterOptions(
      FILTER_OPTIONS.filter(
        (filter) => !filters.find((selectedFilter) => selectedFilter.source === filter.value),
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  function addFilter(
    selectedFilterSource: keyof typeof EmailReportFilterSource,
    addFn: (defaultValue?: EmailReportFilterPropsUnion, insertIndex?: number | undefined) => void,
  ) {
    switch (selectedFilterSource) {
      case EmailReportFilterSource.location:
        addFn({
          source: EmailReportFilterSource.location,
          evaluation: EmailReportFilterEvaluation.contains,
          stores: [],
        })
        break

      case EmailReportFilterSource.folder:
        addFn({
          source: EmailReportFilterSource.folder,
          evaluation: EmailReportFilterEvaluation.contains,
          folders: [],
        })
        break
      case EmailReportFilterSource.publisher:
        addFn({
          source: EmailReportFilterSource.publisher,
          evaluation: EmailReportFilterEvaluation.contains,
          publishers: [],
        })
        break
    }
  }

  function handleSelectFilterChange(
    filterValue: keyof typeof EmailReportFilterSource,
    addFn: (defaultValue?: EmailReportFilterPropsUnion, insertIndex?: number | undefined) => void,
  ) {
    const selectedFilter = FILTER_OPTIONS.find((option) => option.value === filterValue)

    if (selectedFilter) {
      setFilterOptions((options) => options.filter((option) => option.value !== filterValue))
      addFilter(filterValue, addFn)
    }
  }

  return (
    <FormList name={name}>
      {(fields, { add, remove }) => (
        <div className={styles.container}>
          {fields.map((field) => (
            <FormItem
              validation="reports.notification.filter"
              valuePropName="filter"
              noMargin
              {...field}
            >
              <FilterComponent
                selectedReports={selectedReports}
                filterOptions={filterOptions}
                onDelete={remove}
              />
            </FormItem>
          ))}

          {filterOptions.length !== 0 && (
            <Select
              dropdownMatchSelectWidth={false}
              style={{ width: 'fit-content' }}
              disabled={disabled}
              options={filterOptions}
              onChange={(filterOption) => handleSelectFilterChange(filterOption, add)}
              value={`+ ${t(`${I18N_PREFIX}.addFilterButton`)}`}
            />
          )}
        </div>
      )}
    </FormList>
  )
}
